import {
  Avatar,
  Card,
  CardActionArea,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { FunctionComponent, useEffect, useState } from "react";
import { Inistitution } from "./institution";
import { TokenUserHelper } from "../common/services/token-user-service";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import TableChartIcon from "@mui/icons-material/TableChart";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import FolderZipIcon from "@mui/icons-material/FolderZip";
import VideoFileIcon from "@mui/icons-material/VideoFile";
import ImageIcon from "@mui/icons-material/Image";
import YouTubeIcon from "@mui/icons-material/YouTube";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import SharedFileService from "../schoolPrograms/sharedFiles/sharedFileService";
import { ISharedFile } from "../schoolPrograms/sharedFiles/sharedFilesModel";
import VideoService, { IYoutubeVideo } from "../common/services/videoService";
import { toast } from "react-toastify";
const BLUE = "#1565c0";
const BG = "#f0f4ff";

// ─── Design tokens ──────────────────────────────────────────────────────────
const cardSx = {
  bgcolor: "#fff",
  borderRadius: 3,
  boxShadow: "0 1px 4px rgba(0,0,0,0.07), 0 4px 16px rgba(0,0,0,0.04)",
  overflow: "hidden",
};

const rowHover = {
  transition: "background-color 0.15s",
  "&:hover": { bgcolor: "rgba(21,101,192,0.04)", cursor: "pointer" },
};

// ─── StatCard ────────────────────────────────────────────────────────────────
const StatCard = ({
  icon,
  label,
  value,
  href,
  color = BLUE,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  href?: string;
  color?: string;
}) => (
  <Card
    component={href ? "a" : "div"}
    href={href}
    elevation={0}
    sx={{
      ...cardSx,
      textDecoration: "none",
      display: "block",
      transition: "transform 0.18s, box-shadow 0.18s",
      "&:hover": href
        ? {
            transform: "translateY(-2px)",
            boxShadow:
              "0 4px 12px rgba(21,101,192,0.14), 0 8px 24px rgba(21,101,192,0.08)",
          }
        : {},
    }}
  >
    <Box sx={{ p: 2.5, display: "flex", alignItems: "center", gap: 2 }}>
      <Avatar
        sx={{
          bgcolor: `${color}18`,
          color,
          width: 48,
          height: 48,
          borderRadius: 2,
        }}
      >
        {icon}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="h5" fontWeight={800} sx={{ lineHeight: 1, mb: 0.25 }}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>
          {label}
        </Typography>
      </Box>
      {href && (
        <ArrowForwardIosIcon
          sx={{ fontSize: 12, color: "text.disabled", flexShrink: 0 }}
        />
      )}
    </Box>
  </Card>
);

// ─── Panel wrapper ────────────────────────────────────────────────────────────
const Panel = ({
  title,
  icon,
  loading,
  empty,
  action,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  loading?: boolean;
  empty?: boolean;
  action?: React.ReactNode;
  children?: React.ReactNode;
}) => (
  <Box sx={{ ...cardSx, height: "100%", display: "flex", flexDirection: "column" }}>
    <Box
      sx={{
        px: 2.5,
        py: 1.75,
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        bgcolor: "#fafbff",
        borderBottom: "1px solid rgba(21,101,192,0.08)",
      }}
    >
      <Box sx={{ color: BLUE, display: "flex" }}>{icon}</Box>
      <Typography
        variant="subtitle2"
        fontWeight={700}
        sx={{ flexGrow: 1, letterSpacing: 0.2 }}
      >
        {title}
      </Typography>
      {action}
    </Box>
    {loading ? (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress size={22} thickness={4} sx={{ color: BLUE }} />
      </Box>
    ) : empty ? (
      <Typography
        variant="body2"
        color="text.disabled"
        sx={{ px: 2.5, py: 3, fontStyle: "italic" }}
      >
        Nothing to show yet.
      </Typography>
    ) : (
      children
    )}
  </Box>
);

// ─── File type badge ────────────────────────────────────────────────────────
type FileTypeMeta = { icon: React.ReactNode; color: string; bg: string };

const getFileTypeMeta = (fileName: string): FileTypeMeta => {
  const ext = fileName.split(".").pop()?.toLowerCase() ?? "";
  const map: Record<string, FileTypeMeta> = {
    pdf:  { icon: <PictureAsPdfIcon sx={{ fontSize: 20 }} />, color: "#c62828", bg: "#ffebee" },
    doc:  { icon: <DescriptionIcon  sx={{ fontSize: 20 }} />, color: "#1565c0", bg: "#e3f2fd" },
    docx: { icon: <DescriptionIcon  sx={{ fontSize: 20 }} />, color: "#1565c0", bg: "#e3f2fd" },
    xls:  { icon: <TableChartIcon   sx={{ fontSize: 20 }} />, color: "#2e7d32", bg: "#e8f5e9" },
    xlsx: { icon: <TableChartIcon   sx={{ fontSize: 20 }} />, color: "#2e7d32", bg: "#e8f5e9" },
    ppt:  { icon: <SlideshowIcon    sx={{ fontSize: 20 }} />, color: "#e65100", bg: "#fff3e0" },
    pptx: { icon: <SlideshowIcon    sx={{ fontSize: 20 }} />, color: "#e65100", bg: "#fff3e0" },
    zip:  { icon: <FolderZipIcon    sx={{ fontSize: 20 }} />, color: "#6a1b9a", bg: "#f3e5f5" },
    rar:  { icon: <FolderZipIcon    sx={{ fontSize: 20 }} />, color: "#6a1b9a", bg: "#f3e5f5" },
    mp4:  { icon: <VideoFileIcon    sx={{ fontSize: 20 }} />, color: "#00695c", bg: "#e0f2f1" },
    jpg:  { icon: <ImageIcon        sx={{ fontSize: 20 }} />, color: "#ad1457", bg: "#fce4ec" },
    jpeg: { icon: <ImageIcon        sx={{ fontSize: 20 }} />, color: "#ad1457", bg: "#fce4ec" },
    png:  { icon: <ImageIcon        sx={{ fontSize: 20 }} />, color: "#ad1457", bg: "#fce4ec" },
  };
  return map[ext] ?? { icon: <InsertDriveFileOutlinedIcon sx={{ fontSize: 20 }} />, color: BLUE, bg: BG };
};

const FileTypeBadge = ({ fileName }: { fileName: string }) => {
  const { icon, color, bg } = getFileTypeMeta(fileName);
  return (
    <Box
      sx={{
        width: 36,
        height: 36,
        borderRadius: 1.5,
        bgcolor: bg,
        color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      {icon}
    </Box>
  );
};

// ─── FileRow ─────────────────────────────────────────────────────────────────
const FileRow = ({
  file,
  downloadUrl,
  fmt,
}: {
  file: ISharedFile;
  downloadUrl: string;
  fmt: (d: Date) => string;
}) => (
  <ListItem
    disablePadding
    secondaryAction={
      <Tooltip title="Download">
        <IconButton
          size="small"
          component="a"
          href={downloadUrl}
          target="_blank"
          sx={{
            color: "text.disabled",
            transition: "color 0.15s, background-color 0.15s",
            "&:hover": { color: BLUE, bgcolor: BG },
          }}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    }
    sx={{ ...rowHover }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.1,
        pr: 6,
        width: "100%",
      }}
    >
      <FileTypeBadge fileName={file.fileName} />
      <Box sx={{ overflow: "hidden", flex: 1 }}>
        <Typography variant="body2" fontWeight={500} noWrap>
          {file.fileName}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {fmt(file.uploadedOn)}
        </Typography>
      </Box>
    </Box>
  </ListItem>
);

// ─── NavRow ───────────────────────────────────────────────────────────────────
const NavRow = ({
  href,
  icon,
  primary,
  secondary,
}: {
  href: string;
  icon: React.ReactNode;
  primary: string;
  secondary?: string;
}) => (
  <ListItem disablePadding sx={{ ...rowHover }}>
    <CardActionArea
      component="a"
      href={href}
      sx={{
        px: 2,
        py: 1.1,
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        transition: "background-color 0.15s",
        "&:hover": { bgcolor: "transparent" },
      }}
    >
      <Box sx={{ color: BLUE, display: "flex", flexShrink: 0 }}>{icon}</Box>
      <ListItemText
        primary={
          <Typography variant="body2" fontWeight={500}>
            {primary}
          </Typography>
        }
        secondary={
          secondary && (
            <Typography variant="caption" color="text.secondary">
              {secondary}
            </Typography>
          )
        }
        sx={{ my: 0 }}
      />
      <ArrowForwardIosIcon
        sx={{ fontSize: 11, color: "text.disabled", flexShrink: 0 }}
      />
    </CardActionArea>
  </ListItem>
);

// ─── ViewAllLink ─────────────────────────────────────────────────────────────
const ViewAllLink = ({ href, label }: { href: string; label: string }) => (
  <Box sx={{ px: 2.5, py: 1, bgcolor: "#fafbff", borderTop: "1px solid rgba(21,101,192,0.08)" }}>
    <CardActionArea
      component="a"
      href={href}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        borderRadius: 1,
        px: 0.75,
        py: 0.35,
        color: BLUE,
        width: "auto",
        transition: "background-color 0.15s",
        "&:hover": { bgcolor: BG },
      }}
    >
      <Typography variant="caption" fontWeight={700}>
        {label}
      </Typography>
      <ArrowForwardIosIcon sx={{ fontSize: 10 }} />
    </CardActionArea>
  </Box>
);

// ─── Main Component ───────────────────────────────────────────────────────────
export const InistitutionHome: FunctionComponent = () => {
  const tokenUser = TokenUserHelper.Get();
  const hasMagazine = tokenUser?.AbhyasaBharathiMagazine;

  const [eabhyasFiles, setEabhyasFiles] = useState<ISharedFile[]>([]);
  const [schoolFiles, setSchoolFiles] = useState<ISharedFile[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [youtubeVideos, setYoutubeVideos] = useState<IYoutubeVideo[]>([]);
  const [loadingVideos, setLoadingVideos] = useState(true);

  useEffect(() => {
    if (!sessionStorage.getItem("welcomed")) {
      toast.success(`Welcome back, ${tokenUser?.SchoolName || "School"}! 👋`, {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      sessionStorage.setItem("welcomed", "1");
    }
  }, []);

  useEffect(() => {
    Promise.all([
      SharedFileService.getEbhyasFiles(),
      SharedFileService.getSchoolFiles(),
    ])
      .then(([eResp, sResp]) => {
        const sorted = (list: ISharedFile[]) =>
          [...list]
            .sort(
              (a, b) =>
                new Date(b.uploadedOn).getTime() -
                new Date(a.uploadedOn).getTime()
            )
            .slice(0, 5);
        setEabhyasFiles(sorted(eResp.data?.list ?? []));
        setSchoolFiles(sorted(sResp.data?.list ?? []));
      })
      .catch(() => {})
      .finally(() => setLoadingFiles(false));
  }, []);

  useEffect(() => {
    VideoService.getLatest(6)
      .then((res) => setYoutubeVideos(res.data?.list ?? []))
      .catch(() => {})
      .finally(() => setLoadingVideos(false));
  }, []);

  const fmt = (d: Date) =>
    new Date(d).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

  return (
    <Inistitution>
      {/* ── Viewport-filling 2-col layout ── */}
      <Box sx={{ height: "100%", display: "flex", gap: 2.5 }}>

        {/* ── LEFT: stat cards + file panels ── */}
        <Box sx={{ width: "42%", flexShrink: 0, display: "flex", flexDirection: "column", gap: 2, minHeight: 0 }}>

          {/* Stat cards */}
          <Grid container spacing={1.5} sx={{ flexShrink: 0 }}>
            <Grid item xs={4}>
              <StatCard icon={<FolderSharedIcon />} label="eAbhyas Files" value={eabhyasFiles.length} color={BLUE} />
            </Grid>
            <Grid item xs={4}>
              <StatCard icon={<FolderSharedIcon />} label="School Files" value={schoolFiles.length} color="#e65100" />
            </Grid>
            <Grid item xs={4}>
              <StatCard icon={<FolderSharedIcon />} label="Total Files" value={eabhyasFiles.length + schoolFiles.length} href="#/School/SharedFiles" color="#2e7d32" />
            </Grid>
          </Grid>

          {/* eAbhyas Files panel */}
          <Box sx={{ flex: 1, ...cardSx, display: "flex", flexDirection: "column", minHeight: 0 }}>
            <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1, bgcolor: "#fafbff", borderBottom: "1px solid rgba(21,101,192,0.08)", flexShrink: 0 }}>
              <Box sx={{ color: BLUE, display: "flex" }}><InsertDriveFileOutlinedIcon fontSize="small" /></Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>Recent eAbhyas Files</Typography>
            </Box>
            {loadingFiles ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={20} thickness={4} sx={{ color: BLUE }} /></Box>
            ) : eabhyasFiles.length === 0 ? (
              <Typography variant="body2" color="text.disabled" sx={{ px: 2, py: 2, fontStyle: "italic" }}>Nothing to show yet.</Typography>
            ) : (
              <>
                <List disablePadding sx={{ flex: 1, overflow: "auto" }}>
                  {eabhyasFiles.map((f) => (
                    <FileRow key={f.fileId} file={f} downloadUrl={SharedFileService.getFileDownloadUrl(f.fileId)} fmt={fmt} />
                  ))}
                </List>
                <ViewAllLink href="#/School/SharedFiles" label="View all files" />
              </>
            )}
          </Box>

          {/* School Files panel */}
          <Box sx={{ flex: 1, ...cardSx, display: "flex", flexDirection: "column", minHeight: 0 }}>
            <Box sx={{ px: 2, py: 1.5, display: "flex", alignItems: "center", gap: 1, bgcolor: "#fafbff", borderBottom: "1px solid rgba(21,101,192,0.08)", flexShrink: 0 }}>
              <Box sx={{ color: BLUE, display: "flex" }}><FolderSharedIcon fontSize="small" /></Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>Recent School Files</Typography>
            </Box>
            {loadingFiles ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={20} thickness={4} sx={{ color: BLUE }} /></Box>
            ) : schoolFiles.length === 0 ? (
              <Typography variant="body2" color="text.disabled" sx={{ px: 2, py: 2, fontStyle: "italic" }}>Nothing to show yet.</Typography>
            ) : (
              <>
                <List disablePadding sx={{ flex: 1, overflow: "auto" }}>
                  {schoolFiles.map((f) => (
                    <FileRow key={f.fileId} file={f} downloadUrl={SharedFileService.getFileDownloadUrl(f.fileId)} fmt={fmt} />
                  ))}
                </List>
                <ViewAllLink href="#/School/SharedFiles" label="View all files" />
              </>
            )}
          </Box>

        </Box>

        {/* ── RIGHT: YouTube panel ── */}
        <Box sx={{ flex: 1, ...cardSx, display: "flex", flexDirection: "column", minHeight: 0, minWidth: 0 }}>
          {/* Header */}
          <Box sx={{ px: 2.5, py: 1.5, display: "flex", alignItems: "center", gap: 1.25, bgcolor: "#fafbff", borderBottom: "1px solid rgba(21,101,192,0.08)", flexShrink: 0 }}>
            <YouTubeIcon sx={{ color: "#c62828", fontSize: 22 }} />
            <Typography variant="subtitle2" fontWeight={700} sx={{ flexGrow: 1 }}>Latest from eAbhyas Academy</Typography>
            <Chip
              component="a"
              href="https://www.youtube.com/@eabhyasacademy9414"
              target="_blank"
              rel="noopener noreferrer"
              label="Visit Channel"
              icon={<OpenInNewIcon sx={{ fontSize: "14px !important" }} />}
              clickable
              size="small"
              sx={{ fontWeight: 600, fontSize: "0.72rem", transition: "all 0.15s", "&:hover": { transform: "translateY(-1px)" } }}
            />
          </Box>

          {/* Videos grid */}
          {loadingVideos ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={22} thickness={4} sx={{ color: BLUE }} />
            </Box>
          ) : youtubeVideos.length === 0 ? (
            <Typography variant="body2" color="text.disabled" sx={{ px: 2.5, py: 3, fontStyle: "italic" }}>No videos available.</Typography>
          ) : (
            <Grid container sx={{ p: 1.5, flex: 1, overflow: "auto", alignContent: "flex-start" }}>
              {youtubeVideos.map((video) => (
                <Grid item xs={6} md={4} key={video.videoId} sx={{ p: 0.75 }}>
                  <Box
                    component="a"
                    href={`https://www.youtube.com/watch?v=${video.videoId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      display: "flex", flexDirection: "column",
                      borderRadius: 2, overflow: "hidden",
                      border: "1px solid rgba(0,0,0,0.07)",
                      textDecoration: "none", color: "inherit", bgcolor: "#fff",
                      willChange: "transform, box-shadow",
                      transition: "transform 0.28s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.28s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 8px 24px rgba(198,40,40,0.14)",
                        "& .yt-thumb": { transform: "scale(1.06)" },
                        "& .yt-badge": { opacity: 1 },
                      },
                    }}
                  >
                    <Box sx={{ position: "relative", width: "100%", paddingTop: "56.25%", bgcolor: "grey.100", flexShrink: 0, overflow: "hidden" }}>
                      <Box className="yt-thumb" component="img" src={video.thumbnail} alt={video.title}
                        sx={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.4s cubic-bezier(0.25,0.46,0.45,0.94)" }} />
                      <Box className="yt-badge" sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.28)", opacity: 0, transition: "opacity 0.22s ease" }}>
                        <Box sx={{ width: 36, height: 36, borderRadius: "50%", bgcolor: "#c62828", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.4)" }}>
                          <YouTubeIcon sx={{ fontSize: 20, color: "#fff" }} />
                        </Box>
                      </Box>
                    </Box>
                    <Box sx={{ p: 1, flex: 1, display: "flex", flexDirection: "column", gap: 0.4 }}>
                      <Typography variant="body2" fontWeight={600}
                        sx={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.4, fontSize: "0.78rem", flex: 1 }}>
                        {video.title}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {new Date(video.published).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

      </Box>

    </Inistitution>
  );
};
